import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "../utils/swal";

import WorkWithUs from "../components/sections/WorkWithUs";
import AirportTransfers from "../components/booking/AirportTransfers";
import CityTours from "../components/booking/CityTours";
import OutstationTrip from "../components/booking/OutstationTrip";
// import EventBooking from "../components/booking/EventBooking";
// import CorporateBooking from "../components/booking/CorporateBooking";
import InfoModal from "../components/booking/InfoModal";
import PolicyModal from "../components/booking/PolicyModal";
import VehicleInfoModal from "../components/booking/VehicleInfoModal";
import DateSelectModal from "../components/booking/DateSelectModal";
import HoursSelectModal from "../components/booking/HoursSelectModal";
import LocationModel from "../components/booking/LocationModel";
import { applyBookingAvailabilityPayload } from "../utils/bookingLeadTime";
const RIDE_AIRPORT = 1;
const RIDE_CITYTOUR = 2;
const RIDE_OUTSTATION = 3;

const BOOKING_VALID_TYPES = ["airport", "city", "outstation"];

/** Shown while banner API loads and when no CMS content exists for the active tab. */
function BookingPageBannerFallback() {
  return (
    <div className="tabRight_text" style={{ width: "450px" , height: "50%" }}>
      {/* Content removed */}
    </div>
  );
}

export default function BookingPage() {
  const [bannerContent, setBannerContent] = useState(null);
  const [newBookingEnabled, setNewBookingEnabled] = useState(true);
  const [rebookEditData, setRebookEditData] = useState(null);
  const location = useLocation();
  const editDataFromState = location.state?.editData;
  const checkoutRevisionRef =
    typeof location.state?.checkoutRevisionRef === "number"
      ? location.state.checkoutRevisionRef
      : 0;
  const { type, id: rebookId } = useParams();
  const navigate = useNavigate();
  const editData = editDataFromState || rebookEditData;
  /** Must follow URL :type — stale useState(initialTab) broke EDIT-from-checkout when the route changed but the tab state did not. */
  const activeTab = (() => {
    if (BOOKING_VALID_TYPES.includes(type)) return type;
    const rideType = Number(editData?.ride_type);
    if (rideType === RIDE_CITYTOUR) return "city";
    if (rideType === RIDE_OUTSTATION) return "outstation";
    return "airport";
  })();

  // Date/Hours selection bridge
  const [activeSetter, setActiveSetter] = useState(null);
  const [datePickerConfig, setDatePickerConfig] = useState({});
  const datePickerInitialRef = useRef("");
  const [activeHoursSetter, setActiveHoursSetter] = useState(null);

  // Booking data to be passed to checkout
  const [bookingData, setBookingData] = useState(null);

  /** Only clear stale quote once per navigation — not on every editData reference change (that was wiping Check Fare payload). */
  const clearedQuoteForLocationKeyRef = useRef(null);

  // Shared "Check Fare" button state (controlled by active form)
  const [checkFareConfig, setCheckFareConfig] = useState({
    onClick: null,
    disabled: true,
    label: "Check Fare",
  });
  const bookingTabsRef = useRef(null);

  const toDisplayDateTime = (rideDate, pickupTime) => {
    if (!rideDate || !pickupTime) return "";
    const dt = new Date(`${rideDate}T${String(pickupTime).slice(0, 5)}:00`);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  const normalizeStopsForEdit = (stopsList) =>
    (Array.isArray(stopsList) ? stopsList : [])
      .map((s) => ({
        address: s?.address || s?.stop_location || "",
        lat: s?.lat ?? s?.stop_lat ?? null,
        lng: s?.lng ?? s?.stop_lng ?? null,
      }))
      .filter((s) => String(s.address || "").trim() !== "");
  const mapBookingToEditData = (bookingObj) => {
    const ridesData = Array.isArray(bookingObj?.rides_data)
      ? bookingObj.rides_data
      : [];
    const onwardRide = ridesData[0] || null;
    if (!onwardRide) return null;
    const returnRide =
      Number(bookingObj?.booking_type) === 2 && ridesData.length > 1
        ? ridesData[ridesData.length - 1]
        : null;

    return {
      ride_type: Number(bookingObj?.ride_type || 0),
      booking_type: Number(bookingObj?.booking_type || 1),
      no_of_passengers: Number(
        bookingObj?.no_of_passengers || onwardRide?.no_of_passengers || 1,
      ),
      suitcase: Number(bookingObj?.suitcase || onwardRide?.suitcase || 0),
      city_hours: onwardRide?.city_hours || null,
      pickup: onwardRide?.pickup_location || "",
      pickup_lat: onwardRide?.pickup_lat ?? null,
      pickup_lng: onwardRide?.pickup_lng ?? null,
      dropoff: onwardRide?.drop_location || "",
      dropoff_lat: onwardRide?.drop_lat ?? null,
      dropoff_lng: onwardRide?.drop_lng ?? null,
      schedule: toDisplayDateTime(onwardRide?.ride_date, onwardRide?.pickup_time),
      stops: normalizeStopsForEdit(onwardRide?.stops),
      rides: [
        {
          vehicle_type: onwardRide?.vehicle_type_id || null,
        },
      ],
      terminal: onwardRide?.terminal || null,
      flight_no: onwardRide?.flight_no || null,
      return_pickup:
        returnRide?.pickup_location || onwardRide?.return_pickup_location || null,
      return_pickup_lat:
        returnRide?.pickup_lat ?? onwardRide?.return_pickup_lat ?? null,
      return_pickup_lng:
        returnRide?.pickup_lng ?? onwardRide?.return_pickup_lng ?? null,
      return_dropoff:
        returnRide?.drop_location || onwardRide?.return_drop_location || null,
      return_dropoff_lat:
        returnRide?.drop_lat ?? onwardRide?.return_drop_lat ?? null,
      return_dropoff_lng:
        returnRide?.drop_lng ?? onwardRide?.return_drop_lng ?? null,
      return_schedule:
        toDisplayDateTime(returnRide?.ride_date, returnRide?.pickup_time) || null,
      return_stops: normalizeStopsForEdit(
        returnRide?.stops || onwardRide?.return_stops,
      ),
      return_terminal: returnRide?.terminal || onwardRide?.return_terminal || null,
      return_flight_no:
        returnRide?.flight_no || onwardRide?.return_flight_no || null,
      same_dv_multiday: Boolean(bookingObj?.same_dv_multiday),
    };
  };

  /** Entering edit-from-checkout: drop stale quote once per route navigation (location.key). */
  useEffect(() => {
    if (!editData) {
      clearedQuoteForLocationKeyRef.current = null;
      return;
    }
    if (clearedQuoteForLocationKeyRef.current === location.key) return;
    clearedQuoteForLocationKeyRef.current = location.key;
    setBookingData(null);
  }, [editData, location.key]);

  useEffect(() => {
    let isMounted = true;
    const fetchRebookData = async () => {
      if (!rebookId) {
        if (isMounted) setRebookEditData(null);
        return;
      }
      try {
        const token = localStorage.getItem("user_token");
        if (!token) {
          navigate("/login");
          return;
        }
        const response = await axios.get(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/bookings/${rebookId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const bookingObj = response?.data?.booking;
        const mapped = mapBookingToEditData(bookingObj);
        if (isMounted) {
          setRebookEditData(mapped);
        }
      } catch (error) {
        if (isMounted) {
          setRebookEditData(null);
          Swal.fire({
            icon: "error",
            text: "Unable to load booking for re-book.",
          });
          navigate("/bookings");
        }
      }
    };
    fetchRebookData();
    return () => {
      isMounted = false;
    };
  }, [rebookId, navigate]);

  /** Session draft is owned by active form (useLayoutEffect) so preview survives edit → checkout.
   * Do not write BOOKING_DRAFT here: a passive useEffect ran after child layout and overwrote the
   * merged form preview with raw Check Fare `bookingData`, so Policy merge saw stale fields. */

  useEffect(() => {
    fetchBanners();
    const pending = localStorage.getItem("pendingBooking");
    if (pending) {
      const data = JSON.parse(pending);
      setBookingData(data);
      localStorage.removeItem("pendingBooking");

      if (data.ride_type == RIDE_AIRPORT) navigate("/booking/airport", { replace: true });
      else if (data.ride_type == RIDE_OUTSTATION)
        navigate("/booking/outstation", { replace: true });
      else if (data.ride_type == RIDE_CITYTOUR)
        navigate("/booking/city", { replace: true });

      // Auto-open the policy modal so they can proceed
      setTimeout(() => {
        const modalElement = document.getElementById("updatePolicy-Modal");
        if (modalElement && window.bootstrap) {
          const modal = new window.bootstrap.Modal(modalElement);
          modal.show();
        }
      }, 500);
    }
  }, [navigate]);

  useEffect(() => {
    let isMounted = true;
    const fetchBookingAvailability = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_APP_DOMAIN}/api/customer/booking-availability`,
        );
        applyBookingAvailabilityPayload(response?.data);
        const isEnabled = Boolean(response?.data?.new_booking_enabled);
        if (!isMounted) return;
        setNewBookingEnabled(isEnabled);
        if (!isEnabled) {
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "warning",
            title: "New bookings are currently disabled by admin.",
            showConfirmButton: false,
            timer: 2500,
          });
          navigate("/", { replace: true });
        }
      } catch (error) {
        if (!isMounted) return;
        setNewBookingEnabled(true);
      }
    };

    fetchBookingAvailability();
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_APP_DOMAIN}/api/customer/banner_content`,
      );
      if (response.data.status) {
        const content = response.data.content;
        // Backward compatible: if API still returns a single string, use it as fallback for all tabs
        if (typeof content === "string" || content === null) {
          setBannerContent({
            airport: content,
            city: content,
            outstation: content,
            fallback: content,
          });
        } else {
          setBannerContent({
            airport: content.airport ?? content.fallback ?? "",
            city: content.city ?? content.fallback ?? "",
            outstation: content.outstation ?? content.fallback ?? "",
            fallback: content.fallback ?? "",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching banner content:", error);
    }
  };

  const handleTabChange = (tab) => {
    navigate(`/booking/${tab}`, { replace: true });
  };

  const handleBookingTabClick = (tab) => {
    if (!newBookingEnabled) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "New bookings are currently disabled by admin.",
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    handleTabChange(tab);
    if (!localStorage.getItem("selectedCityId")) {
      const el = document.getElementById("locationModal");
      if (el && window.bootstrap) {
        window.bootstrap.Modal.getOrCreateInstance(el).show();
      }
    }
  };

  const currentBannerHtml =
    bannerContent && activeTab
      ? bannerContent[activeTab] || bannerContent.fallback || ""
      : "";

  useEffect(() => {
    const tabsRoot = bookingTabsRef.current;
    if (!tabsRoot) return;
    const activeButton = tabsRoot.querySelector(".nav-link.active");
    if (!activeButton) return;

    // Keep selected tab visible on small screens where tabs are horizontally scrollable.
    activeButton.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeTab]);

  const handleDateConfirm = (dateStr) => {
    if (activeSetter) {
      activeSetter(dateStr);
    }
    setActiveSetter(null);
  };

  const handleHoursConfirm = (hours) => {
    if (activeHoursSetter) {
      activeHoursSetter(hours);
    }
    setActiveHoursSetter(null);
  };

  const openDatePicker = (setter, config = {}, initialValue = "") => {
    datePickerInitialRef.current =
      initialValue != null && String(initialValue).trim() !== ""
        ? String(initialValue).trim()
        : "";
    setActiveSetter(() => setter);
    setDatePickerConfig(config);
  };

  const openHoursPicker = (setter) => {
    setActiveHoursSetter(() => setter);
  };

  return (
    <>
      <section className="transportTab_wrap section_pt section_pb">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 col-md-6 col-sm-12 col-12">
              <div className="transport_tabs">
                <nav className="main_nav">
                  <div
                    className="nav nav-tabs nav-tabs2"
                    id="nav-tab"
                    role="tablist"
                    ref={bookingTabsRef}
                  >
                    <button
                      className={`nav-link${activeTab == "airport" ? " active" : ""}`}
                      type="button"
                      role="tab"
                      onClick={() => handleBookingTabClick("airport")}
                    >
                      Airport Transfers
                    </button>
                    <button
                      className={`nav-link${activeTab == "city" ? " active" : ""}`}
                      type="button"
                      role="tab"
                      onClick={() => handleBookingTabClick("city")}
                    >
                      City Tours
                    </button>
                    <button
                      className={`nav-link${activeTab == "outstation" ? " active" : ""}`}
                      type="button"
                      role="tab"
                      onClick={() => handleBookingTabClick("outstation")}
                    >
                      Outstation Trip
                    </button>
                  </div>
                </nav>
                {/* <p className="booking_form_scroll_hint">
                  Scroll down in this panel for schedule, flight details, and guest
                  info — then tap Check Fare.
                </p> */}
                <div className="booking_tab_scroll_wrap">
                  <div className="tab-content" id="nav-tabContent">
                  {activeTab == "airport" && (
                    <AirportTransfers
                      onOpenDatePicker={openDatePicker}
                      setBookingData={setBookingData}
                      initialData={editData}
                      parentBookingSnapshot={bookingData}
                      checkoutRevisionRef={checkoutRevisionRef}
                      onCheckFareConfigChange={setCheckFareConfig}
                    />
                  )}
                  {activeTab == "city" && (
                    <CityTours
                      onOpenDatePicker={openDatePicker}
                      onOpenHoursPicker={openHoursPicker}
                      setBookingData={setBookingData}
                      initialData={editData}
                      parentBookingSnapshot={bookingData}
                      checkoutRevisionRef={checkoutRevisionRef}
                      onCheckFareConfigChange={setCheckFareConfig}
                    />
                  )}
                  {activeTab == "outstation" && (
                    <OutstationTrip
                      onOpenDatePicker={openDatePicker}
                      setBookingData={setBookingData}
                      initialData={editData}
                      parentBookingSnapshot={bookingData}
                      checkoutRevisionRef={checkoutRevisionRef}
                      onCheckFareConfigChange={setCheckFareConfig}
                    />
                  )}
                  </div>
                </div>
                {/* shared Check Fare button */}
                <div className="check_fare mt-3">
                  <button
                    type="button"
                    className="btn"
                    onClick={checkFareConfig.onClick || undefined}
                    disabled={!checkFareConfig.onClick || checkFareConfig.disabled}
                  >
                    {checkFareConfig.label}
                  </button>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE — Dynamic Content (Static Display) */}
            <div className="col-lg-6 col-md-6 col-sm-12 col-12 d-md-block d-none">
              <div className="transport_tabRight">
                {currentBannerHtml ? (
                  <div className="tabRight_text">
                    <div
                      dangerouslySetInnerHTML={{ __html: currentBannerHtml }}
                    ></div>
                  </div>
                ) : (
                  <BookingPageBannerFallback />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <WorkWithUs />

      {/* Modals */}
      <InfoModal />
      <LocationModel />
      <PolicyModal bookingData={bookingData} />
      <VehicleInfoModal />
      <DateSelectModal
        onConfirm={handleDateConfirm}
        config={datePickerConfig}
        initialValueRef={datePickerInitialRef}
      />
      <HoursSelectModal onConfirm={handleHoursConfirm} />
    </>
  );
}